// src/components/layout/ResponsiveToolbarActions.jsx
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import resolveResponsiveToolbarActions from '../../helpers/mrt/resolveResponsiveToolbarActions';
/**
 * Componente de toolbar responsiva e reutilizável.
 * Passe um array `actions` com objetos:
 * { key, label, tooltip?, color?, variant?, disabled?, onClick?, show? }
 */
function ResponsiveToolbarActions({ actions = [], gap = 1 }) {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const { visibleActions, menuActions } = resolveResponsiveToolbarActions(actions, {
        isMobile: isXs,
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    /**
     * Abre o menu de opções do mobile.
     */
    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Fecha o menu de opções.
     */
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const btnSx = {
        /**
         * No mobile o botão ocupa o espaço disponível,
         * mas deixa espaço para o botão de três pontos.
         */
        flex: { xs: '1 1 auto', md: '0 0 auto' },

        minWidth: { xs: 90, md: 64 },
        whiteSpace: 'nowrap',

        px: { xs: 1.5, md: 2 },
        py: { xs: 0.75, md: 1.25 },

        /**
         * Não utiliza mais 100% no mobile.
         * Isso permite que o menu de três pontos fique na mesma linha.
         */
        width: 'auto',
    };

    /**
     * O Box existe porque o Tooltip precisa de um elemento
     * que possa receber eventos mesmo quando o botão está desabilitado.
     *
     * No mobile ele cresce, mas não ocupa obrigatoriamente a linha inteira.
     */
    const wrapBox = (child) => (
        <Box
            sx={{
                display: 'flex',
                flex: { xs: '1 1 auto', md: '0 0 auto' },
                width: 'auto',
                minWidth: 0,
            }}
        >
            {child}
        </Box>
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                /**
                 * No mobile mantém os elementos na mesma linha.
                 * No desktop pode quebrar normalmente caso existam muitos botões.
                 */
                flexWrap: { xs: 'nowrap', md: 'wrap' },

                gap,
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflowX: 'visible',
            }}
        >
            {visibleActions.map(
                ({ key, label, tooltip, startIcon = undefined, color = 'primary', variant = 'contained', disabled, onClick }) => {
                    // Evita executar um onClick quando o botão estiver desabilitado.
                    const safeOnClick = disabled ? undefined : onClick;

                    return (
                        <Tooltip key={key} arrow placement="top" title={tooltip || ''}>
                            {wrapBox(
                                <Button
                                    color={color}
                                    variant={variant}
                                    startIcon={startIcon}
                                    size={isXs ? 'small' : 'medium'}
                                    sx={btnSx}
                                    onClick={safeOnClick}
                                    disabled={disabled}
                                >
                                    {label}
                                </Button>
                            )}
                        </Tooltip>
                    );
                }
            )}

            {menuActions.length > 0 && (
                <>
                    <IconButton
                        size={isXs ? 'small' : 'medium'}
                        onClick={handleOpenMenu}
                        aria-label="Mais ações"
                        sx={{
                            flex: '0 0 auto',
                        }}
                    >
                        <MoreVertIcon />
                    </IconButton>

                    <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
                        {menuActions.map((action) => (
                            <MenuItem
                                key={action.key}
                                disabled={action.disabled}
                                onClick={() => {
                                    handleCloseMenu();
                                    action.onClick?.();
                                }}
                            >
                                {action.startIcon && <ListItemIcon>{action.startIcon}</ListItemIcon>}

                                <ListItemText>{action.label}</ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    );
}

ResponsiveToolbarActions.propTypes = {
    actions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            tooltip: PropTypes.string,
            color: PropTypes.string,
            startIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            variant: PropTypes.string,
            disabled: PropTypes.bool,
            onClick: PropTypes.func,
            show: PropTypes.bool,
        })
    ),
    gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default memo(ResponsiveToolbarActions);
